/**
 * 赛事接口(第 4 步 R-3):主办方后台 /v1/admin/contests、大赛官网 /v1/portal、评审员 /v1/review。
 * 类型与 apps/api/src/modules/contest 的返回一一对应;页面只认这里的类型。
 */
import type { ImportColumn, ImportResult } from '../ui/table-import.js';
import type { ApiClient } from './client.js';
import { mediaApi } from './media.js';

// ── 批量导入的列(逻辑规格 V-4;模板第二行是示例,导入时跳过) ──
export const REG_IMPORT_COLUMNS: ImportColumn[] = [
  { key: 'name', label: '姓名', required: true, example: '王梦佳' },
  {
    key: 'idNo',
    label: '证件号',
    required: true,
    example: '11010520150316002X',
    aliases: ['身份证号', '身份证号码', '证件号码'],
  },
  { key: 'idType', label: '证件类型', example: '身份证' },
  { key: 'birth', label: '出生日期', required: true, example: '2015-03-16' },
  { key: 'school', label: '学校', example: '实验小学' },
  { key: 'grade', label: '年级', required: true, example: '五年级' },
  { key: 'item', label: '赛项', required: true, example: '图形化创意编程赛' },
  { key: 'guardianPhone', label: '监护人手机', required: true, example: '13800000000' },
  { key: 'guardianName', label: '监护人姓名', example: '王家长' },
  { key: 'channel', label: '归属渠道', example: '', aliases: ['渠道站点码'] },
  { key: 'teachers', label: '指导教师', example: '', aliases: ['指导教师ID', '教师ID'] },
  { key: 'team', label: '队伍', example: '', aliases: ['队伍名称'] },
  { key: 'province', label: '省', example: '北京市' },
  { key: 'city', label: '市', example: '北京市' },
  { key: 'district', label: '区', example: '西城区', aliases: ['区县'] },
  // 选填:已有报名的行(导出名单补几行再导回来)填报名号,对得上就跳过;这一列在 Excel 里要设成文本
  { key: 'number', label: '报名号', example: '', aliases: ['参赛号'] },
];
export const REVIEWER_IMPORT_COLUMNS: ImportColumn[] = [
  { key: 'name', label: '姓名', required: true, example: '刘老师' },
  { key: 'phone', label: '手机', required: true, example: '13900000000', aliases: ['手机号'] },
  { key: 'org', label: '归属机构', required: true, example: '市教研室' },
  { key: 'items', label: '赛项范围', required: true, example: '图形化创意编程赛' },
  { key: 'groups', label: '组别范围', example: '小学高年级组、初中组' },
];
export const AWARD_IMPORT_COLUMNS: ImportColumn[] = [
  {
    key: 'number',
    label: '报名号',
    required: true,
    example: '2609301229130003',
    aliases: ['参赛号', '选手ID'],
  },
  { key: 'name', label: '姓名', example: '王梦佳' },
  { key: 'group', label: '组别', example: '小学高年级组' },
  { key: 'award', label: '奖项', required: true, example: '一等奖' },
  { key: 'score', label: '得分', example: '92' },
  { key: 'certNo', label: '证书编号', example: '', aliases: ['证书号'] },
];
export const OFFLINE_IMPORT_COLUMNS: ImportColumn[] = [
  {
    key: 'number',
    label: '报名号',
    required: true,
    example: '2609301229130003',
    aliases: ['参赛号', '选手ID'],
  },
  { key: 'name', label: '姓名', example: '王梦佳' },
  { key: 'group', label: '评审小组', required: true, example: 'A' },
];
/** 阶段成绩导入(第三方承办 / 改去第三方的选手;闭环 §2):报名号、证件号二选一 */
export const STAGE_SCORE_IMPORT_COLUMNS: ImportColumn[] = [
  { key: 'number', label: '报名号', example: '2609301229130003', aliases: ['参赛号', '选手ID'] },
  { key: 'idNo', label: '证件号', example: '', aliases: ['身份证号', '证件号码'] },
  { key: 'name', label: '姓名', example: '王梦佳' },
  { key: 'objective', label: '客观分', required: true, example: '86', aliases: ['成绩', '得分'] },
  { key: 'promoted', label: '是否晋级', example: '', aliases: ['晋级'] },
];
/** 允许补交名单(主办方主动开补交窗口;闭环 §8) */
export const SUPPLEMENT_IMPORT_COLUMNS: ImportColumn[] = [
  { key: 'number', label: '报名号', required: true, example: '2609301229130003', aliases: ['参赛号'] },
  { key: 'name', label: '姓名', example: '王梦佳' },
  { key: 'until', label: '补交截止', example: '2026-10-12 18:00', aliases: ['截止时间'] },
  { key: 'note', label: '备注', example: '评委反映工程缺素材' },
];
/** 纸质证书单号批量导入(C20):证书编号、报名号二选一 */
export const PAPER_IMPORT_COLUMNS: ImportColumn[] = [
  { key: 'certNo', label: '证书编号', example: '2610G-01-B-00001', aliases: ['证书号'] },
  { key: 'number', label: '报名号', example: '', aliases: ['参赛号'] },
  { key: 'expressNo', label: '快递单号', required: true, example: 'SF1234567890', aliases: ['单号'] },
];
/** 指导教师奖 / 团体奖(奖项导入的一种) */
export const SPECIAL_AWARD_IMPORT_COLUMNS: ImportColumn[] = [
  { key: 'kind', label: '类型', required: true, example: '指导教师奖' },
  {
    key: 'target',
    label: '对象',
    required: true,
    example: 'T-261001-0001',
    aliases: ['教师ID', '手机号', '队名', '学校'],
  },
  { key: 'title', label: '奖项名称', example: '优秀指导教师' },
  { key: 'note', label: '备注', example: '' },
];

// ── 字典(与 docs/05 同源的中文名;页面展示用) ──
export const CONTEST_STATUS_LABELS: Record<string, string> = {
  draft: '草稿',
  published: '已发布',
  signup: '报名中',
  running: '进行中',
  publicity: '公示中',
  archived: '已归档',
};
export const CONTEST_STATUS_TONE: Record<string, string> = {
  draft: '',
  published: 'brand',
  signup: 'ok',
  running: 'brand',
  publicity: 'amber',
  archived: '',
};
export const ITEM_FORM_LABELS: Record<string, string> = {
  online_quiz: '答题类',
  work: '作品类',
  mixed: '答题 + 作品',
  offline: '线下赛项',
};
export const ITEM_KIND_LABELS: Record<string, string> = { individual: '个人', team: '团队' };
export const STAGE_KIND_LABELS: Record<string, string> = {
  preliminary: '初赛',
  semifinal: '复赛',
  final: '决赛',
};
export const HOSTING_LABELS: Record<string, string> = { platform: '本平台', third_party: '第三方承办' };
export const BOARD_STATUS_LABELS: Record<string, string> = {
  review_incomplete: '评审中',
  provisional: '临时榜',
  confirmed: '已确认',
  publicity: '公示中',
  final: '正式榜',
  certified: '已发证',
  archived: '已归档',
};
export const BOARD_FLOW = ['review_incomplete', 'provisional', 'publicity', 'final', 'certified'] as const;
export const REG_STATUS_LABELS: Record<string, string> = {
  registered: '已报名',
  pending_manual: '待人工审核',
  returned: '退回补资料',
  rejected: '未通过',
  cancelled: '已取消',
};
export const REG_STATUS_TONE: Record<string, string> = {
  registered: 'ok',
  pending_manual: 'amber',
  returned: 'warn',
  rejected: 'bad',
  cancelled: '',
};
export const AWARD_LABELS: Record<string, string> = {
  first: '一等奖',
  second: '二等奖',
  third: '三等奖',
  excellent: '优秀奖',
  none: '不评奖',
};
export const AWARD_TONE: Record<string, string> = {
  first: 'amber',
  second: 'brand',
  third: 'violet',
  excellent: 'ok',
  none: '',
};
export const AWARD_LEVELS = ['first', 'second', 'third', 'excellent'] as const;
export const ORGANIZER_ROLE_LABELS: Record<string, string> = {
  host: '主办单位',
  organizer: '承办单位',
  co_organizer: '协办单位',
  supporter: '支持单位',
  tech: '技术支持',
};
export const ID_TYPE_LABELS: Record<string, string> = {
  id_card: '居民身份证',
  passport: '护照',
  hk_macao: '港澳居民来往内地通行证',
  taiwan: '台湾居民来往大陆通行证',
  other: '其他',
};
export const SCHOOL_SYSTEM_LABELS: Record<string, string> = {
  six_three: '六三制',
  five_four: '五四制',
  twelve_year: '十二年一贯制',
  other: '其他',
};
export const SCHOOL_STAGE_LABELS: Record<string, string> = {
  primary: '小学',
  junior: '初中',
  senior: '高中',
};
export const WORK_STATUS_LABELS: Record<string, string> = {
  not_submitted: '未提交',
  uploaded: '已提交',
  locked: '已截止',
  supplement_pending: '补交待审',
  supplement_open: '补交中',
  supplement_closed: '补交已截止',
  void: '已作废',
};
export const FILE_CHECK_LABELS: Record<string, string> = {
  accepted: '已接收',
  rejected_empty: '已拒收 · 空文件',
  rejected_corrupt: '已拒收 · 文件损坏',
  rejected_format: '已拒收 · 格式不符',
  preview_failed: '预览失败',
};
export const WORK_FORMATS = [
  'sb3',
  'pdf',
  'mp4',
  'mov',
  'zip',
  'png',
  'jpg',
  'webp',
  'gif',
  'py',
  'cpp',
  'txt',
] as const;
export const REVOKE_REASON_LABELS: Record<string, string> = {
  appeal_revised: '申诉改判',
  violation: '查实违规',
  info_error: '信息错误重制',
};
export const CERT_STATUS_LABELS: Record<string, string> = {
  issued: '有效',
  revoked: '已撤销',
  reissued: '已换发新证',
};
/** 撤销原因的后果(撤销弹窗里提示) */
export const REVOKE_REASON_HINTS: Record<string, string> = {
  appeal_revised: '证书撤销,奖项按申诉结果(先改好奖项);再点「批量发证」按新奖项发',
  violation: '证书撤销,奖项一起撤(团队赛项全队);之后批量发证不会再发给他',
  info_error: '当场按现在的报名信息换发新编号的证书;旧证核验显示「已换发新证」与新编号',
};

// ── 共用结构 ──
export interface Organizer {
  role: string;
  names: string;
}
export interface ContestModule {
  key: string;
  kind: 'system' | 'page' | 'list';
  title: string;
  visible: boolean;
  order: number;
  content?: string | null;
}
export interface DisplaySwitches {
  score: boolean;
  rank: boolean;
  awardLine: boolean;
  eCert: boolean;
  paperCert: boolean;
}
export interface CertTemplate {
  title: string;
  issuer: string;
  sealText: string;
  style: 'A' | 'B';
  downloadUntil: string | null;
}
export interface PortalConfig {
  theme: 'navy' | 'blue';
  slogan?: string | null;
  domain?: string | null;
  logoKey?: string | null;
  bannerKey?: string | null;
  blocks: { news: boolean; schedule: boolean; orgs: boolean; items: boolean; past: boolean };
  past?: {
    summary?: string | null;
    cards: { title: string; caption?: string | null; imageKey?: string | null }[];
  } | null;
}
export interface WorkSpecItem {
  key: string;
  name: string;
  required: boolean;
  formats: string[];
  maxMb: number;
}
export interface ReviewDim {
  key: string;
  name: string;
  max: number;
  desc?: string | null;
}
export interface ReviewConfig {
  n: number;
  trim: boolean;
  diffThreshold: number;
  blind: boolean;
  avoid: boolean;
  dims: ReviewDim[];
}
export interface AwardRule {
  level: string;
  ratio: number | null;
  threshold: number;
  cert: boolean;
}
export interface Promotion {
  ratio: number | null;
  threshold: number | null;
}

// ── 主办方后台 ──
export interface ContestView {
  id: string;
  site: string;
  code2: string;
  name: string;
  intro: string | null;
  signupStart: string | null;
  signupEnd: string | null;
  publicityDays: number;
  appealDays: number;
  organizers: Organizer[];
  portal: PortalConfig;
  modules: ContestModule[];
  inviteRequired: boolean;
  maxItems: number;
  display: DisplaySwitches;
  cert: CertTemplate;
  status: string;
  effectiveStatus: string;
  publishedAt: string | null;
  archivedAt: string | null;
  /** 最近一次平台解锁归档(时间与理由) */
  unlockedAt: string | null;
  unlockReason: string | null;
}
export interface ContestListItem extends ContestView {
  items: number;
  enabledItems: number;
  registrations: number;
  pendingReview: number;
}
export interface StageView {
  id: string;
  seq: number;
  kind: string;
  name: string;
  startAt: string | null;
  endAt: string | null;
  /** 评审截止(作品 / 混合阶段;过了没交的任务提醒评委与主办方) */
  reviewDueAt: string | null;
  form: string;
  ratioObjective: number | null;
  ratioReview: number | null;
  ratioIssue: string | null;
  hosting: string;
  hostingName: string | null;
  hostingUrl: string | null;
  examId: string | null;
  /** 已建考试的答题阶段还没进考试的人数(组别对不上考场被拒的在这里;null = 不适用) */
  examPending?: number | null;
  durationMin: number | null;
  promotion: Promotion | null;
  boardStatus: string;
  boardAt: string | null;
  publicityStart: string | null;
  publicityEnd: string | null;
  appealEnd: string | null;
  finalizedAt: string | null;
  certifiedAt: string | null;
}
export interface ItemView {
  id: string;
  code2: string;
  seq: number;
  name: string;
  subject: string;
  form: string;
  kind: string;
  teamMin: number | null;
  teamMax: number | null;
  enabled: boolean;
  operator: string | null;
  platformUrl: string | null;
  groups: string[];
  intro: string | null;
  workSpec: WorkSpecItem[] | null;
  supplementHours: number;
  review: ReviewConfig | null;
  awardRules: AwardRule[] | null;
  stages: StageView[];
  issues: string[];
  registered: number;
  autoDisabled?: boolean;
}
export interface InviteBatchView {
  id: string;
  batchNo: string;
  itemIds: string[] | null;
  count: number;
  issuedTo: string | null;
  expiresAt: string | null;
  used: number;
  createdAt: string;
}
export interface ContestDetail extends ContestView {
  items: ItemView[];
  invites: InviteBatchView[];
  counts: {
    registrations: number;
    pendingManual: number;
    returned: number;
    cancelled: number;
    reviewers: number;
  };
  publishIssues: string[];
  /** 报名期与比赛期的提醒(不拦发布):各赛项第一个阶段开始早于报名截止的 */
  scheduleWarnings?: string[];
  /** 归档前置没满足的地方(空 = 可以归档) */
  archiveIssues: string[];
}
export interface ContestInput {
  site: string;
  code2: string;
  name: string;
  intro?: string | null;
  signupStart?: string | null;
  signupEnd?: string | null;
  publicityDays?: number;
  appealDays?: number;
  organizers?: Organizer[];
  inviteRequired?: boolean;
  maxItems?: number;
  display?: DisplaySwitches;
  cert: {
    title?: string;
    issuer: string;
    sealText: string;
    style?: 'A' | 'B';
    downloadUntil?: string | null;
  };
  portal?: Partial<PortalConfig>;
}
export interface ItemInput {
  name: string;
  subject: string;
  form: string;
  kind?: string;
  teamMin?: number | null;
  teamMax?: number | null;
  operator?: string | null;
  platformUrl?: string | null;
  groups: string[];
  intro?: string | null;
  workSpec?: WorkSpecItem[] | null;
  supplementHours?: number;
  review?: ReviewConfig | null;
  awardRules?: AwardRule[] | null;
}
export interface StageInput {
  kind: string;
  name: string;
  startAt?: string | null;
  endAt?: string | null;
  form: string;
  ratioObjective?: number | null;
  ratioReview?: number | null;
  confirmZeroObjective?: boolean;
  hosting?: string;
  hostingName?: string | null;
  hostingUrl?: string | null;
  reviewDueAt?: string | null;
  durationMin?: number | null;
  promotion?: Promotion | null;
}
export interface RegistrationInfo {
  province: string;
  city: string;
  district: string;
  school: string;
  schoolSystem: string;
  stage: string;
  grade: string;
}
export interface HistoryLine {
  at: string;
  by: string | null;
  action: string;
  note?: string | null;
}
export interface TeacherBrief {
  id?: string;
  teacherNo: string;
  name: string;
  orgName: string;
}
export interface RegistrationView {
  id: string;
  contestId: string;
  itemId: string;
  itemName: string;
  itemForm: string;
  itemKind: string;
  studentId: string;
  group: string;
  requestedGroup: string | null;
  number: string | null;
  source: string;
  status: string;
  review: string;
  reviewReason: string | null;
  reviewNote: string | null;
  reviewedAt: string | null;
  info: RegistrationInfo;
  teamName: string | null;
  isCaptain: boolean;
  teachers: TeacherBrief[];
  inviteState: string;
  hostingRoute: string;
  hostingNote: string | null;
  cancelledAt: string | null;
  history: HistoryLine[];
  createdAt: string;
}
export interface AdminRegistration extends RegistrationView {
  student: { id: string; name: string; nickname: string };
  inviteCode: string | null;
  /** 团队赛项:队伍有效人数与人数范围提示(不足 / 超员);个人赛项为 null */
  teamSize: number | null;
  teamIssue: string | null;
  /** 团队赛项的队伍邀请码(T-6;帮选手核对用;上线前组的老队伍还没生成时为空) */
  teamCode: string | null;
}
/** 团队赛项的队伍信息(个人中心;别人的姓名是脱敏的) */
export interface TeamInfo {
  size: number;
  min: number | null;
  max: number | null;
  captain: string;
  members: { name: string; isCaptain: boolean; self: boolean }[];
  /** 还没有队员通过审核(没有队长):全队先等审核 */
  waiting: boolean;
  /** 队伍邀请码(T-6):队友报名时填它加入 */
  code: string | null;
  /** 队长在报名截止前可以重置(旧码作废) */
  canResetCode: boolean;
}
/** 作品页上的队伍信息(队员只读看本队作品) */
export interface WorkTeamInfo {
  name: string;
  isCaptain: boolean;
  /** 还没有队员通过审核(没有队长):等待审核 */
  waiting?: boolean;
  min: number | null;
  max: number | null;
  captain: string;
  members: { name: string; isCaptain: boolean }[];
}
export interface AdminTeacher {
  id: string;
  teacherNo: string;
  name: string;
  orgName: string;
  students: number;
  approved: number;
}
export interface PreviewInfo {
  kind: 'image' | 'pdf' | 'video' | 'sb3' | 'text' | 'zip' | 'none';
  imageKey: string | null;
  sprites?: number;
  blocks?: number;
  variables?: number;
  spriteNames?: string[];
  entries?: number;
  /** 压缩包里的前 30 个文件名 */
  entryNames?: string[];
  lines?: number;
  /** 图形化工程的积木结构(派生 JSON 文件,按需取) */
  structureKey?: string | null;
  /** 视频作品转成能在线放的格式:queued 转码中 · done 可在线看 · failed 失败 */
  video?: {
    state: 'queued' | 'done' | 'failed';
    jobId: string | null;
    durationSec?: number | null;
    error?: string | null;
  } | null;
  /** PDF 首页缩略图:queued 生成中 · done 好了(imageKey 就是首页图) · failed 打不开 */
  doc?: {
    state: 'queued' | 'done' | 'failed';
    jobId: string | null;
    pages?: number | null;
    error?: string | null;
  } | null;
  /** 预览生成失败 = 需人工预览(评审下载原件看) */
  manual?: boolean;
  /** 转好的视频:限时播放地址 / 封面地址(读的时候现签) */
  playUrl?: string | null;
  posterUrl?: string | null;
}
export interface WorkFileView {
  specKey: string;
  name: string;
  size: number;
  mediaKey: string;
  format: string;
  check: string;
  message: string | null;
  preview: PreviewInfo;
}
export interface BatchView {
  id: string;
  batchNo: number;
  files: WorkFileView[];
  complete: boolean;
  missing: string[];
  isSupplement: boolean;
  status: string;
  submittedAt: string;
  forReview: boolean;
}
export interface SupplementRequest {
  status: 'pending' | 'approved' | 'rejected';
  reason: string;
  requestedAt: string;
  windowEnd?: string | null;
  handledBy?: string | null;
  handledAt?: string | null;
  note?: string | null;
  /** 谁开的窗口:选手申请 / 主办方主动给 */
  by?: 'player' | 'organizer';
}
export interface StageWorkRow {
  entryId: string;
  anonCode: string;
  group: string;
  number: string | null;
  /** 团队赛项一队一行:队名与人数 */
  teamName: string | null;
  teamSize: number | null;
  status: string;
  /** 作废(违规)的理由 */
  voidReason: string | null;
  batches: number;
  reviewBatch: BatchView | null;
  supplement: SupplementRequest | null;
  /** 能不能主动给补交窗口(阶段截止后、没作废、报名有效) */
  canGrant: boolean;
  /** 能不能作废(还没确认成绩) */
  canVoid: boolean;
}
export interface ReviewerView {
  id: string;
  accountId: string;
  name: string;
  phone: string | null;
  orgName: string | null;
  scope: { itemId: string; groups: string[]; itemName: string }[];
  status: string;
  assigned: number;
  submitted: number;
  rechecks: number;
  /** 手上还没交的任务(停用前提示「他还有 n 件没评」) */
  pending: number;
  avg: number | null;
  deviation: number | null;
  deviationWarn: boolean;
}
/** 停用评审员的结果:转给谁几件、收回几件、转不了的原因 */
export interface ReviewerStatusResult {
  reviewers: ReviewerView[];
  transferred: number;
  withdrawn: number;
  skipped: string[];
}
/** 进度页「按评委列出没交的任务」 */
export interface PendingByReviewer {
  reviewerId: string;
  name: string;
  orgName: string | null;
  status: string;
  tasks: {
    taskId: string;
    entryId: string;
    anonCode: string;
    group: string;
    status: string;
    isRecheck: boolean;
    assignedAt: string;
    overdue: boolean;
  }[];
}
/** 复核结论(C9-4):复核人、复核分、结论(评语)、时间 */
export interface RecheckView {
  by: string;
  name?: string | null;
  score: number;
  note?: string | null;
  at: string;
}
export interface ReviewDetailView {
  judges: number;
  trimmed: boolean;
  scores: number[];
  dropped: number[];
  diff: number;
  recheck: RecheckView | null;
}
export interface ReviewProgress {
  stage: {
    id: string;
    name: string;
    endAt: string | null;
    boardStatus: string;
    reviewDueAt: string | null;
  };
  /** 赛事已归档:只读 */
  archived: boolean;
  config: ReviewConfig | null;
  works: number;
  entries: number;
  /** 计数单位:团队赛项「队」,个人「人」 */
  unit?: string;
  unassigned: number;
  fullyReviewed: number;
  tasks: { total: number; submitted: number };
  /** 过了评审截止还没交的任务数 */
  overdue: number;
  /** 降低 N 后多出来的没交任务数(可一键撤销) */
  excess: number;
  reviewers: ReviewerView[];
  /** 按评委列出没交的任务(停用的评委也列) */
  pending: PendingByReviewer[];
  /** 补交换了批次、已交分评的是旧版本:等主办方选「请评委重评」或「保留」 */
  stale: {
    taskId: string;
    entryId: string;
    anonCode: string;
    reviewer: string;
    total: number | null;
    reviewedBatch: number | null;
  }[];
  recheck: {
    entryId: string;
    anonCode: string;
    group: string;
    scores: number[];
    diff: number;
    pending: boolean;
    recheck: RecheckView | null;
    recheckTask: boolean;
  }[];
  flagged: {
    taskId: string;
    entryId: string;
    anonCode: string;
    flag: string;
    note: string | null;
    reviewer: string;
    /** 处理结果:supplement 已要求补交 / resolved 已处理 */
    handled: string | null;
    handledNote: string | null;
    handledAt: string | null;
  }[];
}
export interface BoardRow {
  entryId: string;
  anonCode: string;
  group: string;
  number: string | null;
  name: string;
  nickname: string;
  school: string;
  teamName: string | null;
  /** 团队赛项:是否队长;unit = 排名单位(同队相同) */
  captain: boolean;
  unit: string;
  objective: number | null;
  review: number | null;
  reviewDetail: ReviewDetailView | null;
  total: number | null;
  rank: number | null;
  rankNote: string | null;
  award: string | null;
  awardSource: string | null;
  awardNote: string | null;
  promoted: boolean | null;
  /** 晋级的人工增删 / 导入带的「是否晋级」(空 = 按规则) */
  promoteOverride: boolean | null;
  promoteNote: string | null;
  /** 客观分来自阶段成绩导入 */
  imported: boolean;
  pendingRecheck: boolean;
  hasWork: boolean;
  /** 评审用的是补交批次:第几批(不是补交的为 null) */
  supplementBatch: number | null;
  /** 作品作废(违规)的理由 */
  voidReason: string | null;
  certificate: string | null;
}
export interface BoardAppeal {
  id: string;
  number: string;
  kind: string;
  text: string;
  status: string;
  verdictNote: string | null;
  handledAt: string | null;
  createdAt: string;
  entryId: string;
  name: string;
  anonCode: string;
  award: string | null;
  objective: number | null;
  review: number | null;
  total: number | null;
}
export interface SpecialAward {
  id: string;
  kind: string;
  basis: string;
  targetName: string;
  title: string;
  note: string | null;
}
export interface Board {
  contest: {
    id: string;
    name: string;
    publicityDays: number;
    appealDays: number;
    display: DisplaySwitches;
    site: string;
    status: string;
  };
  /** 赛事已归档:只读 */
  archived: boolean;
  item: {
    id: string;
    name: string;
    form: string;
    kind?: string;
    teamMin?: number | null;
    teamMax?: number | null;
    groups: string[];
    awardRules: AwardRule[] | null;
    review: ReviewConfig | null;
  };
  /** 提醒(不拦出榜):团队赛项人数不在范围的队伍 */
  warnings?: string[];
  stage: {
    id: string;
    name: string;
    form: string;
    seq: number;
    ratioObjective: number | null;
    ratioReview: number | null;
    boardStatus: string;
    boardAt: string | null;
    publicityStart: string | null;
    publicityEnd: string | null;
    appealEnd: string | null;
    finalizedAt: string | null;
    certifiedAt: string | null;
    promotion: Promotion | null;
    examId: string | null;
    endAt: string | null;
    hosting: string;
    hostingName: string | null;
  };
  awardStage: boolean;
  nextStage: string | null;
  issues: string[];
  /** 线下赛:确认前要已锁定 */
  offline: { lockedAt: string | null; writtenBackAt: string | null } | null;
  /** 能不能导入阶段成绩(有客观分的阶段、出榜前) */
  canImportScores: boolean;
  canGenerate: boolean;
  canFinalize: boolean;
  /** 晋级阶段确认后能不能撤回(不能时给原因);不适用为 null */
  unconfirm: { allowed: boolean; reason: string | null } | null;
  rows: BoardRow[];
  distribution: Record<string, number>;
  /** 人工调整后超过规则比例的档(C12 ②:允许,只提醒) */
  ratioWarnings?: string[];
  appeals: BoardAppeal[];
  certificates: number;
  /** 指导教师奖 / 团体奖(导入的;评奖阶段) */
  specialAwards: SpecialAward[];
  promotedToNext?: number;
  removedFromNext?: number;
}
export interface CertListItem {
  id: string;
  number: string;
  award: string;
  awardLabel: string;
  holderName: string;
  group: string;
  status: string;
  revokeReason: string | null;
  /** 信息错误重制换发到的新编号 */
  replacedBy: string | null;
  issuedAt: string;
  verifyPath: string;
}
export interface PaperRow {
  id: string;
  number: string | null;
  holderName: string;
  awardLabel: string;
  certNumber: string;
  recipient: string;
  phone: string;
  address: string;
  status: string;
  expressNo: string | null;
  shippedAt: string | null;
  createdAt: string;
  /** 申领超过 30 天还没寄出 */
  overdue: boolean;
}
export interface AdminNotice {
  id: string;
  title: string;
  summary: string | null;
  content: string | null;
  kind: string;
  pinned: boolean;
  status: string;
  publishedAt: string | null;
  stageId: string | null;
  rows: number | null;
}

/** 主办方看板 · 官网与报名转化(《埋点与统计口径》§3:UV 按账号或浏览器会话去重;转化 = 提交人数 ÷ 报名页访问人数) */
export interface ContestTraffic {
  days: number;
  pv: number;
  uv: number;
  modules: { dim: string; title: string; n: number; uv: number }[];
  signupUv: number;
  submitted: number;
  submitters: number;
  /** 官网自助报名的人(不论看没看过报名页) */
  selfSubmitters: number;
  /** 看过报名页后自助报名的人(转化的分子) */
  converted: number;
  conversion: number | null;
  sources: { dim: string; n: number; uv: number }[];
  series: { day: string; n: number; uv: number }[];
}

export function contestAdminApi(api: ApiClient) {
  const B = '/v1/admin/contests';
  return {
    list: () => api.get<ContestListItem[]>(B),
    create: (body: ContestInput) => api.post<ContestDetail>(B, body),
    /** 复制上届(C1-2):带配置与赛项 / 阶段结构,不带题目与名单;shiftDays 为空 = 时间清空 */
    copy: (id: string, body: { name: string; site: string; code2?: string; shiftDays: number | null }) =>
      api.post<ContestDetail>(`${B}/${id}/copy`, body),
    get: (id: string) => api.get<ContestDetail>(`${B}/${id}`),
    update: (id: string, body: Partial<ContestInput>) => api.patch<ContestDetail>(`${B}/${id}`, body),
    publish: (id: string) => api.post<ContestDetail>(`${B}/${id}/publish`, {}),
    archive: (id: string) => api.post<ContestDetail>(`${B}/${id}/archive`, {}),
    modules: (id: string, modules: ContestModule[]) =>
      api.put<ContestDetail>(`${B}/${id}/modules`, { modules }),
    /** 官网与报名转化(埋点) */
    traffic: (id: string, days: number) => api.get<ContestTraffic>(`${B}/${id}/traffic`, { query: { days } }),
    createItem: (id: string, body: ItemInput) => api.post<ItemView>(`${B}/${id}/items`, body),
    updateItem: (itemId: string, body: Partial<ItemInput>) =>
      api.patch<ItemView>(`${B}/items/${itemId}`, body),
    setEnabled: (itemId: string, enabled: boolean) =>
      api.post<ItemView>(`${B}/items/${itemId}/enabled`, { enabled }),
    createStage: (itemId: string, body: StageInput) =>
      api.post<ItemView>(`${B}/items/${itemId}/stages`, body),
    updateStage: (stageId: string, body: Partial<StageInput>) =>
      api.patch<ItemView>(`${B}/stages/${stageId}`, body),
    deleteStage: (stageId: string) => api.delete<ItemView>(`${B}/stages/${stageId}`),
    createStageExam: (stageId: string, body: { paperId: string; durationMin?: number }) =>
      api.post<{ examId: string; created: number; skipped: number; errors: string[] }>(
        `${B}/stages/${stageId}/exam`,
        body,
      ),
    syncStageExam: (stageId: string) =>
      api.post<{ created: number; skipped: number; errors: string[] }>(
        `${B}/stages/${stageId}/exam/sync`,
        {},
      ),
    notices: (id: string) => api.get<AdminNotice[]>(`${B}/${id}/notices`),
    createNotice: (
      id: string,
      body: {
        title: string;
        summary?: string | null;
        content?: string | null;
        pinned: boolean;
        publish: boolean;
      },
    ) => api.post<AdminNotice[]>(`${B}/${id}/notices`, body),
    updateNotice: (
      id: string,
      noticeId: string,
      body: {
        title: string;
        summary?: string | null;
        content?: string | null;
        pinned: boolean;
        publish: boolean;
      },
    ) => api.put<AdminNotice[]>(`${B}/${id}/notices/${noticeId}`, body),
    deleteNotice: (id: string, noticeId: string) =>
      api.delete<AdminNotice[]>(`${B}/${id}/notices/${noticeId}`),
    createInvites: (
      id: string,
      body: { itemIds?: string[] | null; count: number; issuedTo?: string | null; expiresAt?: string | null },
    ) => api.post<{ batchId: string; batchNo: string; count: number }>(`${B}/${id}/invites`, body),
    exportInvites: (batchId: string) => api.blob(`${B}/invites/${batchId}/export`),
    registrations: (id: string, q: { itemId?: string; group?: string; status?: string; q?: string } = {}) =>
      api.get<AdminRegistration[]>(`${B}/${id}/registrations`, { query: q }),
    reviewRegistration: (regId: string, action: 'approve' | 'return' | 'reject', note?: string) =>
      api.post<RegistrationView>(`${B}/registrations/${regId}/review`, { action, ...(note ? { note } : {}) }),
    setHosting: (regId: string, route: string, note: string) =>
      api.post<{ ok: boolean }>(`${B}/registrations/${regId}/hosting`, { route, note }),
    cancelRegistration: (regId: string, reason: string) =>
      api.post<{ ok: boolean }>(`${B}/registrations/${regId}/cancel`, { reason }),
    /** 主办方代改报名信息(理由必填,留痕;改年级 / 组别按新年级判组别或指定组别) */
    modifyRegistration: (
      regId: string,
      body: { info?: RegistrationInfo; teacherNos?: string[]; group?: string; reason: string },
    ) => api.patch<RegistrationView>(`${B}/registrations/${regId}`, body),
    teachers: (id: string) => api.get<AdminTeacher[]>(`${B}/${id}/teachers`),
    // 批量导入(逻辑规格 V-4):dryRun 走 …/check 只预检;正式导入再预检一遍,全部通过才入库
    importRegistrations: (id: string, rows: Record<string, string>[], dryRun: boolean) =>
      api.post<ImportResult>(`${B}/${id}/import/registrations${dryRun ? '/check' : ''}`, { rows }),
    importReviewers: (id: string, rows: Record<string, string>[], dryRun: boolean) =>
      api.post<ImportResult>(`${B}/${id}/import/reviewers${dryRun ? '/check' : ''}`, { rows }),
    importAwards: (stageId: string, rows: Record<string, string>[], dryRun: boolean) =>
      api.post<ImportResult>(`${B}/stages/${stageId}/import/awards${dryRun ? '/check' : ''}`, { rows }),
    importStageScores: (stageId: string, rows: Record<string, string>[], dryRun: boolean) =>
      api.post<ImportResult>(`${B}/stages/${stageId}/import/scores${dryRun ? '/check' : ''}`, { rows }),
    importSupplements: (stageId: string, rows: Record<string, string>[], dryRun: boolean) =>
      api.post<ImportResult>(`${B}/stages/${stageId}/import/supplements${dryRun ? '/check' : ''}`, { rows }),
    importPapers: (id: string, rows: Record<string, string>[], dryRun: boolean) =>
      api.post<ImportResult>(`${B}/${id}/import/papers${dryRun ? '/check' : ''}`, { rows }),
    importSpecialAwards: (stageId: string, rows: Record<string, string>[], dryRun: boolean) =>
      api.post<ImportResult>(`${B}/stages/${stageId}/import/special-awards${dryRun ? '/check' : ''}`, {
        rows,
      }),
    stageWorks: (stageId: string) => api.get<StageWorkRow[]>(`${B}/stages/${stageId}/works`),
    handleSupplement: (entryId: string, body: { approve: boolean; hours?: number; note?: string }) =>
      api.post<{ ok: boolean }>(`${B}/entries/${entryId}/supplement`, body),
    /** 主办方主动给补交窗口(阶段截止后;until 是 ISO 时间) */
    grantSupplement: (entryId: string, until: string, note?: string) =>
      api.post<{ ok: boolean; entryId: string; supplement: SupplementRequest }>(
        `${B}/entries/${entryId}/supplement-window`,
        { until, ...(note ? { note } : {}) },
      ),
    voidWork: (workId: string, reason: string) =>
      api.post<{ ok: boolean }>(`${B}/works/${workId}/void`, { reason }),
    /** 整件作品作废(违规):撤销没交的评审任务、已交的分不进榜、选手看到理由 */
    voidEntry: (entryId: string, reason: string) =>
      api.post<{ ok: boolean; withdrawnTasks: number; boardReset: boolean }>(`${B}/entries/${entryId}/void`, {
        reason,
      }),
    /** 评委标记的问题作品:要求补交(给补交窗口)/ 标记已处理 */
    handleFlag: (taskId: string, body: { action: 'supplement' | 'resolved'; note: string; until?: string }) =>
      api.post<{ ok: boolean }>(`${B}/review-tasks/${taskId}/flag`, body),
    reviewers: (id: string, stageId?: string) =>
      api.get<ReviewerView[]>(`${B}/${id}/reviewers`, { query: { stageId } }),
    saveReviewer: (
      id: string,
      body: {
        phone: string;
        name: string;
        orgName?: string | null;
        scope: { itemId: string; groups: string[] }[];
      },
    ) => api.post<ReviewerView[]>(`${B}/${id}/reviewers`, body),
    /** 停用时他手上没交的任务一并收回;给 transferTo 就一键转交给那位评委 */
    reviewerStatus: (reviewerId: string, status: 'active' | 'disabled', transferTo?: string | null) =>
      api.post<ReviewerStatusResult>(`${B}/reviewers/${reviewerId}/status`, {
        status,
        ...(transferTo ? { transferTo } : {}),
      }),
    assign: (stageId: string, mode: 'balanced' | 'random') =>
      api.post<{
        assigned: number;
        entries: number;
        shortage: { anonCode: string; need: number }[];
        avoided: number;
      }>(`${B}/stages/${stageId}/review/assign`, { mode }),
    progress: (stageId: string) => api.get<ReviewProgress>(`${B}/stages/${stageId}/review/progress`),
    recheck: (entryId: string, reviewerId: string) =>
      api.post<{ ok: boolean }>(`${B}/entries/${entryId}/recheck`, { reviewerId }),
    /** 重分配给…(没交的任务;过回避与评审范围) */
    reassign: (taskId: string, reviewerId: string) =>
      api.post<{ ok: boolean; taskId: string }>(`${B}/review-tasks/${taskId}/reassign`, { reviewerId }),
    /** 撤销一条没交的任务(不再算未评完;理由留痕) */
    withdrawTask: (taskId: string, reason: string) =>
      api.post<{ ok: boolean }>(`${B}/review-tasks/${taskId}/withdraw`, { reason }),
    /** 补交后评的是旧版本的评分:请评委重评 / 保留 */
    staleTask: (taskId: string, action: 'reopen' | 'keep') =>
      api.post<{ ok: boolean }>(`${B}/review-tasks/${taskId}/stale`, { action }),
    /** 手动指派(过回避与评审范围;满 N 位不再加) */
    manualAssign: (stageId: string, entryId: string, reviewerId: string) =>
      api.post<{ ok: boolean; taskId: string }>(`${B}/stages/${stageId}/review/manual`, {
        entryId,
        reviewerId,
      }),
    /** 降低 N 后一键撤销多出来的没交任务 */
    trimExcess: (stageId: string) =>
      api.post<{ withdrawn: number }>(`${B}/stages/${stageId}/review/trim`, {}),
    /** 评审截止后:超期没交的任务一键收回重分配 */
    reclaim: (stageId: string) =>
      api.post<{ moved: number; withdrawn: number; shortage: string[] }>(
        `${B}/stages/${stageId}/review/reclaim`,
        {},
      ),
    board: (stageId: string) => api.get<Board>(`${B}/stages/${stageId}/board`),
    generate: (stageId: string) => api.post<Board>(`${B}/stages/${stageId}/board/generate`, {}),
    adjust: (entryId: string, award: string, note: string) =>
      api.post<Board>(`${B}/entries/${entryId}/award`, { award, note }),
    /** 晋级名单人工增删(确认前;理由必填留痕) */
    setPromotion: (entryId: string, promoted: boolean, note: string) =>
      api.post<Board>(`${B}/entries/${entryId}/promotion`, { promoted, note }),
    /** 晋级 0 人要带 force 二次确认 */
    confirm: (stageId: string, force = false) =>
      api.post<Board>(`${B}/stages/${stageId}/board/confirm`, force ? { force: true } : {}),
    /** 撤回晋级确认(下一阶段还没开始、没有记录时) */
    unconfirm: (stageId: string, reason: string) =>
      api.post<Board>(`${B}/stages/${stageId}/board/unconfirm`, { reason }),
    finalize: (stageId: string) => api.post<Board>(`${B}/stages/${stageId}/board/finalize`, {}),
    handleAppeal: (
      appealId: string,
      body: {
        verdict: 'upheld' | 'rejected';
        note: string;
        award?: string;
        objective?: number;
        review?: number;
      },
    ) => api.post<Board>(`${B}/appeals/${appealId}/handle`, body),
    issue: (stageId: string) =>
      api.post<{ issued: number; total: number }>(`${B}/stages/${stageId}/certificates`, {}),
    certificates: (stageId: string) => api.get<CertListItem[]>(`${B}/stages/${stageId}/certificates`),
    revoke: (certId: string, reason: string) =>
      api.post<{ ok: boolean; reissued?: { number: string }; revoked?: number }>(
        `${B}/certificates/${certId}/revoke`,
        { reason },
      ),
    papers: (id: string) => api.get<PaperRow[]>(`${B}/${id}/papers`),
    ship: (paperId: string, expressNo: string) =>
      api.post<{ ok: boolean }>(`${B}/papers/${paperId}/ship`, { expressNo }),
  };
}
export type ContestAdminApi = ReturnType<typeof contestAdminApi>;

// ── 平台后台 · 赛事归档解锁(闭环 §6:平台管理员,理由 ≥ 5 字,留痕;解锁后重新公示) ──
export interface ArchivedContest {
  id: string;
  name: string;
  site: string;
  status: string;
  tenant: { name: string; code: string };
  archivedAt: string | null;
  unlockedAt: string | null;
  unlockReason: string | null;
}
export function platformContestApi(api: ApiClient) {
  const P = '/v1/platform/contests';
  return {
    list: () => api.get<ArchivedContest[]>(P),
    unlock: (id: string, reason: string) =>
      api.post<{ ok: boolean; republished: number }>(`${P}/${id}/unlock`, { reason }),
  };
}

// ── 大赛官网 ──
export interface ScheduleStep {
  key: string;
  name: string;
  startAt: string | null;
  endAt: string | null;
  note: string;
  state: 'unset' | 'todo' | 'current' | 'done';
}
export interface PortalItem {
  id: string;
  name: string;
  subject: string;
  form: string;
  kind: string;
  teamMin: number | null;
  teamMax: number | null;
  groups: string[];
  groupLabels: string[];
  operator: string | null;
  intro: string | null;
  state: 'open' | 'soon' | 'ended' | 'closed';
  stateLabel: string;
  stages: {
    id: string;
    kind: string;
    name: string;
    startAt: string | null;
    endAt: string | null;
    form: string;
    hosting: string;
    hostingName: string | null;
  }[];
}
export interface NoticeBrief {
  id: string;
  title: string;
  summary: string | null;
  kind: string;
  pinned: boolean;
  publishedAt: string | null;
}
export interface PortalSite {
  /** 赛事 id(官网埋点用) */
  id: string;
  site: string;
  name: string;
  intro: string | null;
  tenantCode: string;
  status: string;
  signupStart: string | null;
  signupEnd: string | null;
  signupState: 'unset' | 'not_started' | 'open' | 'ended';
  inviteRequired: boolean;
  maxItems: number;
  organizers: Organizer[];
  portal: PortalConfig;
  modules: { key: string; kind: string; title: string }[];
  /** 首页区块实际显示哪些(主办方开关 +「通知公告」「往届回顾」菜单没隐藏;C16) */
  homeBlocks: { news: boolean; schedule: boolean; orgs: boolean; items: boolean; past: boolean };
  /** 「比赛报名」入口开着没有(主办方隐藏了「比赛报名」模块 = 全站不出报名入口;C16 ①) */
  signupEntry: boolean;
  schedule: ScheduleStep[];
  items: PortalItem[];
  notices: NoticeBrief[];
  serverTime: string;
}
export interface PublicityRow {
  rank: number | null;
  nickname: string;
  maskedName: string;
  group: string;
  award: string;
  note: string | null;
}
export interface NoticeDetail {
  id: string;
  title: string;
  summary: string | null;
  content: string | null;
  kind: string;
  publishedAt: string | null;
  payload: { rows: PublicityRow[]; itemName: string; stageName: string } | null;
  publicity: {
    itemId: string;
    itemName: string;
    stageName: string;
    boardStatus: string;
    publicityStart: string | null;
    publicityEnd: string | null;
    appealEnd: string | null;
    awardRules: { level: string; label: string; ratio: number | null; threshold: number }[];
  } | null;
}
export interface TokenPair {
  accessToken: string;
  accessExpiresAt: number;
  refreshToken: string;
  refreshExpiresAt: number;
}
export interface PortalPlayer {
  studentId: string;
  name: string;
  maskedName: string;
  nickname: string;
  birthMonth: string | null;
  schoolStage: string | null;
  grade: string | null;
  school: string | null;
  idType: string;
  idNoMasked: string;
  email: string | null;
  guardianName: string | null;
  guardianPhone: string | null;
}
export interface StageAction {
  kind: 'exam' | 'submit' | 'external' | 'offline' | 'none';
  label: string;
  enabled: boolean;
  hint: string;
}
export interface StageEntry {
  id: string;
  kind: string;
  name: string;
  form: string;
  startAt: string | null;
  endAt: string | null;
  window: 'unset' | 'not_started' | 'open' | 'ended';
  hosting: string;
  hostingUrl: string | null;
  entryId: string | null;
  workStatus: string | null;
  /** 作品作废(违规)的理由:显示「已作废:理由」 */
  voidReason: string | null;
  promoted: boolean | null;
  action: StageAction;
}
export interface MyRegistration extends RegistrationView {
  inviteCode: string | null;
  canModify: boolean;
  /** 报名期内能取消(截止后不能) */
  canCancel: boolean;
  /** 被退回补资料:截止后到第一个阶段开始前,只能补资料(改报名信息与指导教师) */
  resubmitOnly: boolean;
  team: TeamInfo | null;
  stages: StageEntry[];
}
export interface PortalMe {
  /** passwordInitial:还在用初始密码(证件号后六位),个人中心顶部提示修改 */
  account: { id: string; name: string; phone: string; passwordInitial?: boolean };
  players: PortalPlayer[];
  teacher: {
    id: string;
    teacherNo: string;
    name: string;
    orgName: string;
    idNoMasked: string;
    createdAt: string;
  } | null;
  contest: {
    id: string;
    site: string;
    name: string;
    signupEnd: string | null;
    editable: boolean;
    /** 赛事已归档:只读 */
    archived: boolean;
    inviteRequired: boolean;
    maxItems: number;
  };
  registrations: MyRegistration[];
}
/** 考试列表里的作品提交入口(A23-3):和官网个人中心打开同一个作品页 /c/:site/work/:entryId */
export interface ContestWorkEntry {
  contest: { id: string; site: string; name: string };
  item: { id: string; name: string };
  number: string | null;
  teamName: string | null;
  stage: { id: string; name: string; startAt: string | null; endAt: string | null; window: string };
  entryId: string;
  workStatus: string | null;
  action: StageAction;
}
export interface WorkView {
  entryId: string;
  /** 团队赛项:队长交,队员只读看本队作品 */
  team: WorkTeamInfo | null;
  readonly: boolean;
  contest: { id: string; site: string; name: string };
  item: { id: string; name: string; form: string; spec: WorkSpecItem[]; supplementHours: number };
  stage: { id: string; name: string; startAt: string | null; endAt: string | null };
  group: string;
  number: string | null;
  status: string;
  /** 作废(违规)的理由 */
  voidReason: string | null;
  canUpload: boolean;
  uploadBlockedReason: string;
  inSupplementWindow: boolean;
  supplement: SupplementRequest | null;
  canApplySupplement: boolean;
  reviewWorkId: string | null;
  batches: BatchView[];
  serverTime: string;
}
export interface FileCheckResult {
  accepted: WorkFileView[];
  rejected: { specKey: string; name: string; check: string; message: string }[];
}
export interface ResultCard {
  registrationId: string;
  number: string | null;
  name: string;
  item: { id: string; name: string; form: string; kind?: string };
  teamName: string | null;
  group: string;
  groupLabel: string;
  stages: { id: string; name: string; boardStatus: string; entered: boolean; promoted: boolean | null }[];
  published: boolean;
  boardStatus: string | null;
  publicityStart: string | null;
  publicityEnd: string | null;
  appealEnd: string | null;
  entryId: string | null;
  award: string | null;
  awardLabel: string | null;
  display: DisplaySwitches;
  score: number | null;
  scoreNote: string | null;
  rank: number | null;
  rankNote: string | null;
  awardLine: string | null;
  certificate: {
    id: string;
    number: string;
    status: string;
    canDownload: boolean;
    downloadUntil: string | null;
    verifyPath: string;
  } | null;
  paper: {
    canRequest: boolean;
    /** 寄出前本人能改收件信息 */
    canEdit?: boolean;
    status: string | null;
    expressNo: string | null;
    recipient?: string;
    phone?: string;
    address?: string;
  } | null;
  appeal: { open: boolean; number: string | null; status: string | null; verdictNote: string | null } | null;
}
export interface VerifyResult {
  found: boolean;
  number: string;
  scanned?: boolean;
  valid?: boolean;
  status?: string;
  holder?: string;
  awardLabel?: string;
  contestName?: string;
  itemName?: string;
  groupLabel?: string;
  issuedOn?: string;
  issuer?: string;
  revokeReason?: string | null;
  /** 信息错误重制:换发到的新编号(状态 reissued 时有) */
  replacedBy?: string | null;
}
export interface OwnerCertificate {
  number: string;
  status: string;
  style: 'A' | 'B';
  verifyPath: string;
  snapshot: {
    holderName: string;
    contestName: string;
    itemName: string;
    groupLabel: string;
    awardLabel: string;
    workTitle: string | null;
    issuer: string;
    sealText: string;
    title: string;
    issuedOn: string;
  };
}
export interface TeacherCenter {
  teacher: { teacherNo: string; name: string; orgName: string; idNoMasked: string; createdAt: string };
  /** 我得的指导教师奖(主办方导入;评奖阶段公示后可见) */
  awards: { id: string; itemName: string; title: string; note: string | null }[];
  rows: {
    name: string;
    number: string | null;
    group: string;
    groupLabel: string;
    itemName: string;
    itemForm: string;
    teamName: string | null;
    isCaptain: boolean;
    status: string;
    reviewReason: string | null;
    award: string;
  }[];
}
export interface PlayerRegisterInput {
  name: string;
  nickname?: string;
  email?: string | null;
  phone: string;
  smsCode: string;
  idType: string;
  idNo: string;
  birthMonth?: string;
  guardianName?: string | null;
  guardianPhone?: string | null;
  agree: true;
}
export interface TeacherRegisterInput {
  name: string;
  phone: string;
  smsCode: string;
  idType: string;
  idNo: string;
  orgName: string;
}
export interface SignupInput {
  itemId: string;
  studentId?: string;
  info: RegistrationInfo;
  /** 团队赛项:新建队伍填队名(当队长);加入队伍填队伍邀请码 */
  teamName?: string | null;
  teamCode?: string | null;
  inviteCode?: string | null;
  teacherNos: string[];
  requestGroup?: string | null;
}

export function portalApi(api: ApiClient) {
  const P = '/v1/portal';
  return {
    site: (site: string) => api.get<PortalSite>(`${P}/contests/${site}`),
    page: (site: string, key: string) =>
      api.get<{ key: string; title: string; content: string }>(`${P}/contests/${site}/pages/${key}`),
    notices: (site: string) => api.get<NoticeBrief[]>(`${P}/contests/${site}/notices`),
    notice: (site: string, id: string) => api.get<NoticeDetail>(`${P}/contests/${site}/notices/${id}`),
    registerPlayer: (site: string, body: PlayerRegisterInput) =>
      api.post<{ studentId: string; initialPassword: boolean; tokens: TokenPair }>(
        `${P}/contests/${site}/register/player`,
        body,
      ),
    registerTeacher: (site: string, body: TeacherRegisterInput) =>
      api.post<{
        teacherNo: string;
        name: string;
        orgName: string;
        initialPassword: boolean;
        tokens: TokenPair;
      }>(`${P}/contests/${site}/register/teacher`, body),
    login: (identifier: string, password: string) =>
      api.post<{ tokens: TokenPair; roles: string[]; passwordInitial: boolean; mustChangePassword: boolean }>(
        `${P}/login`,
        { identifier, password },
      ),
    /** 短信验证码登录(先 smsCode(phone, 'login') 发码) */
    loginSms: (phone: string, code: string) =>
      api.post<{ tokens: TokenPair; roles: string[]; passwordInitial: boolean; mustChangePassword: boolean }>(
        `${P}/login/sms`,
        { phone, code },
      ),
    smsCode: (phone: string, purpose: 'register' | 'login') =>
      api.post<{ resendAfterSec: number; devCode?: string }>('/v1/auth/sms-code', { phone, purpose }),
    captcha: () => api.post<{ captchaId: string; image: string }>(`${P}/captcha`, {}),
    verify: (body: { number: string; token?: string; captchaId?: string; captcha?: string }) =>
      api.post<VerifyResult>(`${P}/certificates/verify`, body),
    query: (site: string, number: string, phone4: string) =>
      api.post<ResultCard>(`${P}/contests/${site}/results/query`, { number, phone4 }),
    me: (site: string) => api.get<PortalMe>(`${P}/contests/${site}/me`),
    /** 团队赛项返回队伍邀请码(建队的人当场拿到,发给队友) */
    signup: (site: string, body: SignupInput) =>
      api.post<RegistrationView & { teamCode: string | null }>(`${P}/contests/${site}/registrations`, body),
    /** 报名表上查验邀请码(和提交报名同一套规则,只看不核销) */
    checkInvite: (site: string, itemId: string, code: string) =>
      api.post<{ ok: boolean; required: boolean; message: string }>(
        `${P}/contests/${site}/invite-codes/check`,
        { itemId, code },
      ),
    modify: (
      regId: string,
      body: {
        info?: RegistrationInfo;
        teamName?: string | null;
        teamCode?: string | null;
        teacherNos?: string[];
      },
    ) => api.patch<RegistrationView>(`${P}/registrations/${regId}`, body),
    /** 队长重置队伍邀请码(旧码作废) */
    resetTeamCode: (regId: string) =>
      api.post<{ code: string }>(`${P}/registrations/${regId}/team-code/reset`, {}),
    cancel: (regId: string, reason?: string) =>
      api.post<{ ok: boolean }>(`${P}/registrations/${regId}/cancel`, reason ? { reason } : {}),
    teacherOnBehalf: (body: Omit<TeacherRegisterInput, 'smsCode'>) =>
      api.post<TeacherBrief>(`${P}/teachers`, body),
    lookupTeacher: (no: string) => api.get<TeacherBrief>(`${P}/teachers/${encodeURIComponent(no)}`),
    teacherCenter: (site: string) => api.get<TeacherCenter>(`${P}/contests/${site}/teacher`),
    changePassword: (oldPassword: string, newPassword: string) =>
      api.post<{ ok: boolean }>(`${P}/password`, { oldPassword, newPassword }),
    handoff: (registrationId: string) =>
      api.post<{ code: string; url: string }>(`${P}/handoff`, { registrationId }),
    work: (entryId: string) => api.get<WorkView>(`${P}/entries/${entryId}/work`),
    checkWork: (entryId: string, files: { specKey: string; mediaKey: string; name: string }[]) =>
      api.post<FileCheckResult>(`${P}/entries/${entryId}/works/check`, { files }),
    submitWork: (entryId: string, files: { specKey: string; mediaKey: string; name: string }[]) =>
      api.post<FileCheckResult & { batch: BatchView | null }>(`${P}/entries/${entryId}/works`, { files }),
    selectWork: (entryId: string, workId: string) =>
      api.post<WorkView>(`${P}/entries/${entryId}/works/${workId}/select`, {}),
    applySupplement: (entryId: string, reason: string) =>
      api.post<WorkView>(`${P}/entries/${entryId}/supplement`, { reason }),
    myResults: (site: string) => api.get<ResultCard[]>(`${P}/contests/${site}/results`),
    appeal: (entryId: string, kind: string, text: string) =>
      api.post<{ number: string }>(`${P}/entries/${entryId}/appeal`, { kind, text }),
    certificate: (certId: string) => api.get<OwnerCertificate>(`${P}/certificates/${certId}`),
    paper: (certId: string, body: { recipient: string; phone: string; address: string; agree: true }) =>
      api.post<{ ok: boolean }>(`${P}/certificates/${certId}/paper`, body),
    /** 寄出前改收件信息 */
    updatePaper: (certId: string, body: { recipient: string; phone: string; address: string }) =>
      api.patch<{ ok: boolean }>(`${P}/certificates/${certId}/paper`, body),
    paperReceived: (certId: string) =>
      api.post<{ ok: boolean }>(`${P}/certificates/${certId}/paper/received`, {}),
    /** 作品分片上传:按服务端给的分片大小顺序传,返回媒体 key;onProgress 给 0–1 */
    uploadWork: (file: File, onProgress?: (ratio: number) => void): Promise<string> =>
      mediaApi(api).uploadChunked('work', file, onProgress),
  };
}
export type PortalApi = ReturnType<typeof portalApi>;

// ── 评审员 ──
export interface MyTaskGroup {
  stage: { id: string; name: string; boardStatus: string; reviewDueAt: string | null };
  item: { id: string; name: string };
  contest: { id: string; name: string };
  /** 评审表维度(批量打分表格用) */
  dims: ReviewDim[];
  /** 成绩已确认 / 赛事已归档:只读 */
  locked: boolean;
  total: number;
  submitted: number;
  tasks: {
    id: string;
    anonCode: string;
    group: string;
    groupLabel: string;
    status: string;
    total: number | null;
    flag: string | null;
    isRecheck: boolean;
    /** 评的是补交批次 */
    supplement: boolean;
    /** 评的是第几批(补交的标「补交 · 第 n 批」) */
    batchNo: number | null;
    /** 过了评审截止还没交 */
    overdue: boolean;
    submittedAt: string | null;
  }[];
}
export interface TaskDetail {
  id: string;
  status: string;
  isRecheck: boolean;
  anonCode: string;
  group: string;
  groupLabel: string;
  item: { id: string; name: string; spec: WorkSpecItem[] };
  stage: { id: string; name: string; reviewDueAt: string | null };
  dims: ReviewDim[];
  blind: boolean;
  identity: { school: string } | null;
  work: {
    id: string;
    batchNo: number;
    isSupplement: boolean;
    missing: string[];
    files: WorkFileView[];
    submittedAt: string;
  } | null;
  scores: Record<string, number> | null;
  total: number | null;
  comment: string | null;
  flag: string | null;
  flagNote: string | null;
  /** 过了评审截止还没交 */
  overdue: boolean;
  locked: boolean;
  nextTaskId: string | null;
  progress: { total: number; submitted: number };
}

export function reviewApi(api: ApiClient) {
  const R = '/v1/review';
  return {
    tasks: () => api.get<MyTaskGroup[]>(`${R}/tasks`),
    task: (id: string) => api.get<TaskDetail>(`${R}/tasks/${id}`),
    submit: (
      id: string,
      body: { scores: Record<string, number>; comment?: string; flag?: string | null; flagNote?: string },
    ) => api.post<{ ok: boolean; total: number }>(`${R}/tasks/${id}/submit`, body),
    batch: (items: { taskId: string; scores: Record<string, number>; comment?: string }[]) =>
      api.post<{ ok: boolean; submitted: number }>(`${R}/tasks/batch-submit`, { items }),
  };
}
export type ReviewApi = ReturnType<typeof reviewApi>;
